import { defineComponent as _defineComponent } from 'vue';
import { createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle, Fragment as _Fragment, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, normalizeClass as _normalizeClass } from "vue";
const _hoisted_1 = { class: "bg-white rounded-none lg:rounded-xl shadow-none lg:shadow-lg border-l-0 border-r-0 lg:border-2 border-t border-b lg:border-t lg:border-b border-gray-200 font-inter" };
const _hoisted_2 = { class: "section-item" };
const _hoisted_3 = { class: "mt-1" };
const _hoisted_4 = { class: "section-item" };
const _hoisted_5 = { class: "mt-1" };
const _hoisted_6 = {
    key: 0,
    class: "section-item"
};
const _hoisted_7 = { class: "mt-3" };
const _hoisted_8 = {
    key: 1,
    class: "section-item"
};
const _hoisted_9 = { class: "mt-3" };
const _hoisted_10 = {
    key: 2,
    class: "section-item"
};
const _hoisted_11 = { class: "flex items-start" };
const _hoisted_12 = { class: "flex-shrink-0" };
const _hoisted_13 = { class: "ml-3" };
const _hoisted_14 = { key: 0 };
const _hoisted_15 = {
    key: 0,
    class: "absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-[2px] rounded-lg z-10"
};
const _hoisted_16 = { class: "text-center px-4" };
const _hoisted_17 = { class: "text-sm font-medium text-gray-700" };
const _hoisted_18 = { class: "text-xs text-gray-500 mt-1" };
import { computed } from "vue";
import { useCheckoutStore } from '@shared/stores/checkout-store';
import OrderTypeSelection from '@shared/components/checkout/OrderTypeSelection.vue';
import CustomerDataForm from '@shared/components/checkout/CustomerDataForm.vue';
import DeliveryAddressForm from '@shared/components/checkout/DeliveryAddressForm.vue';
import TimeSelectionForm from '@shared/components/checkout/TimeSelectionForm.vue';
import DynamicPaymentMethodSelection from '@shared/components/checkout/DynamicPaymentMethodSelection.vue';
import EnhancedOrderSubmitButton from '@shared/components/checkout/EnhancedOrderSubmitButton.vue';
import CheckoutTurnstile from '@shared/components/checkout/CheckoutTurnstile.vue';
import DiscountCodeInput from '@shared/components/checkout/DiscountCodeInput.vue';
import { useTheme } from '@shared/composables/useTheme';
export default /*@__PURE__*/ _defineComponent({
    __name: 'CheckoutRightSidebar',
    props: {
        orderType: {},
        customer: {},
        timeSelection: {},
        paymentMethod: {},
        deliveryAdditionalInfo: {},
        canSubmit: { type: Boolean },
        loading: { type: Boolean },
        totalAmount: {},
        formatPrice: { type: Function },
        error: {},
        orderCreated: { type: Boolean },
        createdOrder: {},
        orderData: {},
        subtotal: {},
        deliveryFee: {},
        serviceFee: {},
        openingHoursValidation: {},
        isBlockedByOpeningHours: { type: Boolean }
    },
    emits: ["update:orderType", "update:customer", "update:timeSelection", "update:paymentMethod", "open-contact-modal", "open-address-modal", "open-time-modal", "payment-method-selected", "submit", "payment-success", "payment-error", "payment-cancel"],
    setup(__props, { emit: __emit }) {
        const checkoutStore = useCheckoutStore();
        const { theme } = useTheme();
        const warningColor = computed(() => theme.value.colors.warning || theme.value.colors.primary);
        const warningContainerStyle = computed(() => ({
            backgroundColor: `${warningColor.value}15`,
            borderColor: `${warningColor.value}55`
        }));
        const props = __props;
        const emit = __emit;
        // Computed properties for safe type casting
        const safeOrderType = computed({
            get: () => props.orderType || "pickup",
            set: (value) => emit("update:orderType", value),
        });
        const safePaymentMethod = computed({
            get: () => props.paymentMethod || "manual",
            set: (value) => emit("update:paymentMethod", value),
        });
        const handlePaymentMethodSelected = (method, config) => {
            emit("payment-method-selected", method, config);
        };
        // ✅ True solange die Preisberechnung (Liefergebühr/Summe) noch läuft.
        //    Wird genutzt, um den Bestell-Button zu blockieren.
        const isPricingLoading = computed(() => Boolean(checkoutStore.isLoading));
        const displayTotalAmount = computed(() => {
            const pricingData = checkoutStore.pricingData;
            const totalCent = pricingData?.data?.pricing?.totalCent;
            if (typeof totalCent === 'number') {
                return totalCent / 100;
            }
            return Number.isFinite(props.totalAmount) ? props.totalAmount : 0;
        });
        // ✅ Validation: Check if basic contact data is complete (name, email, phone)
        const hasContactData = computed(() => {
            if (!props.customer)
                return false;
            const hasName = props.customer.name && props.customer.name.trim().length > 0;
            const hasEmail = props.customer.email && props.customer.email.trim().length > 0;
            const hasPhone = props.customer.phone && props.customer.phone.trim().length > 0;
            return hasName && hasEmail && hasPhone;
        });
        // ✅ Validation: Check if delivery address is complete
        const hasDeliveryAddress = computed(() => {
            if (props.orderType !== 'delivery')
                return true; // Not needed for pickup
            if (!props.customer?.address)
                return false;
            const hasStreet = props.customer.address.street && props.customer.address.street.trim().length > 0;
            const hasCity = props.customer.address.city && props.customer.address.city.trim().length > 0;
            const hasPostalCode = props.customer.address.postalCode && props.customer.address.postalCode.trim().length === 5;
            return hasStreet && hasCity && hasPostalCode;
        });
        // ✅ Combined validation: All required data complete
        const isCustomerDataComplete = computed(() => {
            return hasContactData.value && hasDeliveryAddress.value;
        });
        // ✅ Determine which message to show
        const validationMessage = computed(() => {
            if (!hasContactData.value) {
                return {
                    title: 'Kontaktdaten erforderlich',
                    subtitle: 'Bitte füllen Sie zuerst Ihre Kontaktdaten aus'
                };
            }
            if (props.orderType === 'delivery' && !hasDeliveryAddress.value) {
                return {
                    title: 'Lieferadresse erforderlich',
                    subtitle: 'Bitte geben Sie Ihre Lieferadresse an'
                };
            }
            return {
                title: 'Daten erforderlich',
                subtitle: 'Bitte füllen Sie alle erforderlichen Felder aus'
            };
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("section", _hoisted_1, [
                _createCommentVNode(" Order Type Selection "),
                _createElementVNode("div", _hoisted_2, [
                    _createElementVNode("div", _hoisted_3, [
                        _createVNode(OrderTypeSelection, {
                            modelValue: safeOrderType.value,
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((safeOrderType).value = $event))
                        }, null, 8 /* PROPS */, ["modelValue"])
                    ])
                ]),
                _createCommentVNode(" Customer Data "),
                _createElementVNode("div", _hoisted_4, [
                    _createElementVNode("div", _hoisted_5, [
                        _createVNode(CustomerDataForm, {
                            "model-value": _ctx.customer,
                            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => (_ctx.$emit('update:customer', $event))),
                            onOpenModal: _cache[2] || (_cache[2] = ($event) => (_ctx.$emit('open-contact-modal')))
                        }, null, 8 /* PROPS */, ["model-value"])
                    ])
                ]),
                _createCommentVNode(" Delivery Address (nur bei Lieferung) "),
                (_ctx.orderType === 'delivery')
                    ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                        _createElementVNode("div", _hoisted_7, [
                            _createVNode(DeliveryAddressForm, {
                                "model-value": _ctx.customer.address,
                                "show-form": _ctx.orderType === 'delivery',
                                "additional-info": _ctx.deliveryAdditionalInfo,
                                onOpenModal: _cache[3] || (_cache[3] = ($event) => (_ctx.$emit('open-address-modal')))
                            }, null, 8 /* PROPS */, ["model-value", "show-form", "additional-info"])
                        ])
                    ]))
                    : _createCommentVNode("v-if", true),
                _createCommentVNode(" Time Selection (nur wenn innerhalb der Öffnungszeiten) "),
                (!_ctx.openingHoursValidation || _ctx.openingHoursValidation.isWithinHours)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                        _createElementVNode("div", _hoisted_9, [
                            _createVNode(TimeSelectionForm, {
                                "model-value": _ctx.timeSelection,
                                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => (_ctx.$emit('update:timeSelection', $event))),
                                "order-type": safeOrderType.value,
                                onOpenModal: _cache[5] || (_cache[5] = ($event) => (_ctx.$emit('open-time-modal')))
                            }, null, 8 /* PROPS */, ["model-value", "order-type"])
                        ])
                    ]))
                    : _createCommentVNode("v-if", true),
                _createCommentVNode(" Opening Hours Warning "),
                (_ctx.openingHoursValidation && !_ctx.openingHoursValidation.isWithinHours)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                        _createElementVNode("div", {
                            class: "p-4 border",
                            style: _normalizeStyle(warningContainerStyle.value)
                        }, [
                            _createElementVNode("div", _hoisted_11, [
                                _createElementVNode("div", _hoisted_12, [
                                    (_openBlock(), _createElementBlock("svg", {
                                        class: "w-5 h-5 mt-0.5",
                                        style: _normalizeStyle({ color: warningColor.value }),
                                        fill: "none",
                                        stroke: "currentColor",
                                        viewBox: "0 0 24 24"
                                    }, [...(_cache[12] || (_cache[12] = [
                                            _createElementVNode("path", {
                                                "stroke-linecap": "round",
                                                "stroke-linejoin": "round",
                                                "stroke-width": "2",
                                                d: "M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                            }, null, -1 /* CACHED */)
                                        ]))], 4 /* STYLE */))
                                ]),
                                _createElementVNode("div", _hoisted_13, [
                                    _createElementVNode("h3", {
                                        class: "text-sm font-semibold mb-1",
                                        style: _normalizeStyle({ color: warningColor.value })
                                    }, [
                                        _createCommentVNode(" Pickup: Always show as blocked if outside hours "),
                                        (_ctx.orderType === 'pickup')
                                            ? (_openBlock(), _createElementBlock("span", _hoisted_14, " Abholung nicht möglich "))
                                            : (_ctx.orderType === 'delivery')
                                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                                    _createCommentVNode(" Delivery: Different messages based on status "),
                                                    _cache[13] || (_cache[13] = _createElementVNode("span", null, " Lieferung nicht möglich ", -1 /* CACHED */))
                                                ], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */))
                                                : _createCommentVNode("v-if", true)
                                    ], 4 /* STYLE */),
                                    _createElementVNode("p", {
                                        class: "text-sm mb-2",
                                        style: _normalizeStyle({ color: warningColor.value })
                                    }, _toDisplayString(_ctx.openingHoursValidation.message), 5 /* TEXT, STYLE */),
                                    _createElementVNode("p", {
                                        class: "text-sm mt-2",
                                        style: _normalizeStyle({ color: warningColor.value })
                                    }, [...(_cache[14] || (_cache[14] = [
                                            _createElementVNode("strong", null, "Hinweis:", -1 /* CACHED */),
                                            _createTextVNode(" Bitte kontaktieren Sie das Restaurant direkt für weitere Informationen. ", -1 /* CACHED */)
                                        ]))], 4 /* STYLE */)
                                ])
                            ])
                        ], 4 /* STYLE */)
                    ]))
                    : _createCommentVNode("v-if", true),
                _createCommentVNode(" Payment Method "),
                _createElementVNode("div", {
                    class: _normalizeClass(["section-item relative", {
                            'opacity-50 pointer-events-none': _ctx.isBlockedByOpeningHours || !isCustomerDataComplete.value
                        }])
                }, [
                    _createCommentVNode(" ✅ Overlay with message when customer data incomplete "),
                    (!isCustomerDataComplete.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                            _createElementVNode("div", _hoisted_16, [
                                _cache[15] || (_cache[15] = _createElementVNode("i", { class: "fas fa-lock text-gray-400 text-2xl mb-2" }, null, -1 /* CACHED */)),
                                _createElementVNode("p", _hoisted_17, _toDisplayString(validationMessage.value.title), 1 /* TEXT */),
                                _createElementVNode("p", _hoisted_18, _toDisplayString(validationMessage.value.subtitle), 1 /* TEXT */)
                            ])
                        ]))
                        : _createCommentVNode("v-if", true),
                    _createCommentVNode(" Discount Code Input "),
                    _createVNode(DiscountCodeInput, {
                        modelValue: _unref(checkoutStore).discountCode,
                        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event) => ((_unref(checkoutStore).discountCode) = $event)),
                        "discount-info": _unref(checkoutStore).discountInfo,
                        "is-validating": _unref(checkoutStore).discountValidating,
                        "error-message": _unref(checkoutStore).discountError,
                        "from-url": _unref(checkoutStore).discountFromUrl,
                        onApply: _unref(checkoutStore).applyDiscountCode,
                        onRemove: _unref(checkoutStore).removeDiscountCode,
                        onClearError: _unref(checkoutStore).clearDiscountError
                    }, null, 8 /* PROPS */, ["modelValue", "discount-info", "is-validating", "error-message", "from-url", "onApply", "onRemove", "onClearError"]),
                    _createElementVNode("div", null, [
                        _createVNode(DynamicPaymentMethodSelection, {
                            modelValue: safePaymentMethod.value,
                            "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event) => ((safePaymentMethod).value = $event)),
                            "show-debug": false,
                            "delivery-type": safeOrderType.value,
                            onMethodSelected: handlePaymentMethodSelected
                        }, null, 8 /* PROPS */, ["modelValue", "delivery-type"])
                    ])
                ], 2 /* CLASS */),
                _createCommentVNode(" Turnstile + Submit Button "),
                _createElementVNode("div", {
                    class: _normalizeClass(["section-item", { 'opacity-50 pointer-events-none': _ctx.isBlockedByOpeningHours }])
                }, [
                    _createVNode(CheckoutTurnstile),
                    _createVNode(EnhancedOrderSubmitButton, {
                        "can-submit": _ctx.canSubmit && !_ctx.isBlockedByOpeningHours,
                        "is-loading": _ctx.loading,
                        "is-pricing-loading": isPricingLoading.value,
                        "total-amount": displayTotalAmount.value,
                        "format-price": _ctx.formatPrice,
                        error: _ctx.error,
                        "order-success": _ctx.orderCreated,
                        "order-number": _ctx.createdOrder?.orderNumber || _ctx.createdOrder?._id,
                        "payment-method": safePaymentMethod.value,
                        "payment-config": _unref(checkoutStore).paymentMethods,
                        "order-data": _ctx.orderData,
                        "discount-code": _unref(checkoutStore).discountCode,
                        onSubmit: _cache[8] || (_cache[8] = ($event) => (_ctx.$emit('submit'))),
                        onPaymentSuccess: _cache[9] || (_cache[9] = ($event) => (_ctx.$emit('payment-success', $event))),
                        onPaymentError: _cache[10] || (_cache[10] = ($event) => (_ctx.$emit('payment-error', $event))),
                        onPaymentCancel: _cache[11] || (_cache[11] = ($event) => (_ctx.$emit('payment-cancel', $event)))
                    }, null, 8 /* PROPS */, ["can-submit", "is-loading", "is-pricing-loading", "total-amount", "format-price", "error", "order-success", "order-number", "payment-method", "payment-config", "order-data", "discount-code"])
                ], 2 /* CLASS */)
            ]));
        };
    }
});
